import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const TEACHER_LINKS = [
  {
    to: '/teacher/ege-families',
    title: 'Раскладка по семьям',
    description: 'Разложить задачи банка по семьям внутри типа КИМ.',
  },
  {
    to: '/teacher/ege-map',
    title: 'Карта ЕГЭ',
    description: 'Просмотр журнальной карты семей и ступеней (только чтение).',
  },
  {
    to: '/teacher/tasks',
    title: 'Конструктор задач',
    description: 'Создание и правка задач.',
  },
  {
    to: '/teacher/tasks/import',
    title: 'Пакетный импорт',
    description: 'Массовая загрузка задач из JSON.',
  },
  {
    to: '/teacher/tags',
    title: 'Теги',
    description: 'Справочник тегов каталога.',
  },
  {
    to: '/catalog',
    title: 'Каталог',
    description: 'Просмотр банка задач с фильтрами.',
  },
];

const ADMIN_LINKS = [
  {
    to: '/admin/task-cleanup',
    title: 'Очистка банка',
    description: 'Жёсткое удаление лишних задач по типу КИМ.',
  },
  {
    to: '/admin/topic-programs',
    title: 'Программы (темы)',
    description: 'Управление программами тем в меню и каталоге.',
  },
];

function groupIssuesBySource(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.source_name || 'Без сборника';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return Array.from(groups.entries()).map(([sourceName, tasks]) => ({
    sourceName,
    tasks,
  }));
}

export default function TeacherToolsPage() {
  const { user } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [issuesError, setIssuesError] = useState('');

  const loadIssues = useCallback(async () => {
    setIssuesLoading(true);
    setIssuesError('');
    try {
      const res = await api.get('/teacher/ege-bank/statement-issues/');
      const data = Array.isArray(res.data?.results) ? res.data.results : [];
      setIssues(data);
    } catch {
      setIssues([]);
      setIssuesError('Не удалось загрузить список проблем с условием.');
    } finally {
      setIssuesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const issueGroups = useMemo(() => groupIssuesBySource(issues), [issues]);

  return (
    <div className="teacher-tools-page">
      <header className="page-header">
        <h1>Инструменты преподавателя</h1>
        <p className="page-subtitle text-secondary">
          Служебные страницы для банка задач, тегов и программ.
        </p>
      </header>

      <section className="teacher-tools-section" aria-label="Служебные ссылки">
        <ul className="teacher-tools-grid">
          {TEACHER_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className="teacher-tools-card">
                <span className="teacher-tools-card-title">{link.title}</span>
                <span className="teacher-tools-card-desc text-secondary">{link.description}</span>
              </Link>
            </li>
          ))}
          {isSiteAdmin
            ? ADMIN_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="teacher-tools-card">
                    <span className="teacher-tools-card-title">{link.title}</span>
                    <span className="teacher-tools-card-desc text-secondary">{link.description}</span>
                  </Link>
                </li>
              ))
            : null}
        </ul>
      </section>

      <section className="teacher-tools-section" aria-label="Проблемы с условием">
        <div className="teacher-tools-issues-head">
          <h2 className="teacher-tools-section-title">Проблемы с условием</h2>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={loadIssues}
            disabled={issuesLoading}
          >
            Обновить
          </button>
        </div>
        {issuesError ? <div className="alert alert-error">{issuesError}</div> : null}
        {issuesLoading ? (
          <p className="text-secondary">Загрузка…</p>
        ) : issues.length === 0 ? (
          <p className="text-secondary">Помеченных задач пока нет.</p>
        ) : (
          <div className="teacher-tools-issues">
            {issueGroups.map((group) => (
              <div key={group.sourceName} className="teacher-tools-issue-group">
                <h3 className="teacher-tools-issue-source">{group.sourceName}</h3>
                <p className="teacher-tools-issue-numbers">
                  {group.tasks
                    .map((t) => {
                      const num = String(t.source_number || '').trim();
                      return num || `#${t.id}`;
                    })
                    .join(', ')}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
